/**
 * 供应商详情 - 财务分析标签页
 */
import React from 'react';
import { Row, Col, Card } from 'antd';
import { DollarOutlined, RiseOutlined } from '@ant-design/icons';

interface Supplier {
    monthly_purchase_amount?: number;
    quarterly_purchase_amount?: number;
    yearly_purchase_amount?: number;
    total_purchase_amount?: number;
    monthly_sales_revenue?: number;
    quarterly_sales_revenue?: number;
    yearly_sales_revenue?: number;
    total_sales_revenue?: number;
    monthly_gross_profit?: number;
    yearly_gross_profit?: number;
    total_gross_profit?: number;
    gross_profit_margin?: number;
    outstanding_payment?: number;
}

interface Props {
    supplier: Supplier;
}

const StatCard: React.FC<{ label: string; value: number; bgColor: string; borderColor: string; textColor: string; darkColor: string }> =
    ({ label, value, bgColor, borderColor, textColor, darkColor }) => (
        <div style={{ background: bgColor, padding: 16, borderRadius: 8, border: `1px solid ${borderColor}` }}>
            <div style={{ color: textColor, fontSize: 12, marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: darkColor }}>
                ¥{(value || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
            </div>
        </div>
    );

const FinancialTab: React.FC<Props> = ({ supplier }) => {
    const formatCurrency = (value: number | undefined) =>
        `¥${(value || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`;

    return (
        <Card bordered={false}>
            {/* 采购数据 */}
            <div style={{ marginBottom: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
                    <DollarOutlined style={{ fontSize: 18, color: '#52c41a', marginRight: 8 }} />
                    <span style={{ fontSize: 16, fontWeight: 500 }}>采购数据</span>
                </div>
                <Row gutter={[16, 16]}>
                    <Col span={6}><StatCard label="本月采购" value={supplier.monthly_purchase_amount || 0} bgColor="#f6ffed" borderColor="#b7eb8f" textColor="#52c41a" darkColor="#389e0d" /></Col>
                    <Col span={6}><StatCard label="本季度采购" value={supplier.quarterly_purchase_amount || 0} bgColor="#f6ffed" borderColor="#b7eb8f" textColor="#52c41a" darkColor="#389e0d" /></Col>
                    <Col span={6}><StatCard label="本年采购" value={supplier.yearly_purchase_amount || 0} bgColor="#f6ffed" borderColor="#b7eb8f" textColor="#52c41a" darkColor="#389e0d" /></Col>
                    <Col span={6}><StatCard label="历史总采购" value={supplier.total_purchase_amount || 0} bgColor="#e6f7ff" borderColor="#91d5ff" textColor="#1890ff" darkColor="#096dd9" /></Col>
                </Row>
            </div>

            {/* 销售数据 */}
            <div style={{ marginBottom: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
                    <RiseOutlined style={{ fontSize: 18, color: '#f5222d', marginRight: 8 }} />
                    <span style={{ fontSize: 16, fontWeight: 500 }}>销售数据 (人民币)</span>
                </div>
                <Row gutter={[16, 16]}>
                    <Col span={6}><StatCard label="本月销售" value={supplier.monthly_sales_revenue || 0} bgColor="#fff1f0" borderColor="#ffa39e" textColor="#cf1322" darkColor="#a8071a" /></Col>
                    <Col span={6}><StatCard label="本季度销售" value={supplier.quarterly_sales_revenue || 0} bgColor="#fff1f0" borderColor="#ffa39e" textColor="#cf1322" darkColor="#a8071a" /></Col>
                    <Col span={6}><StatCard label="本年销售" value={supplier.yearly_sales_revenue || 0} bgColor="#fff1f0" borderColor="#ffa39e" textColor="#cf1322" darkColor="#a8071a" /></Col>
                    <Col span={6}><StatCard label="历史总销售" value={supplier.total_sales_revenue || 0} bgColor="#e6f7ff" borderColor="#91d5ff" textColor="#1890ff" darkColor="#096dd9" /></Col>
                </Row>
            </div>

            {/* 毛利分析 */}
            <div style={{ marginBottom: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
                    <DollarOutlined style={{ fontSize: 18, color: '#722ed1', marginRight: 8 }} />
                    <span style={{ fontSize: 16, fontWeight: 500 }}>毛利分析</span>
                </div>
                <Row gutter={[16, 16]}>
                    <Col span={6}>
                        <div style={{ background: '#f9f0ff', padding: 16, borderRadius: 8, border: '1px solid #d3adf7' }}>
                            <div style={{ color: '#722ed1', fontSize: 12, marginBottom: 4 }}>本月毛利</div>
                            <div style={{ fontSize: 20, fontWeight: 600, color: (supplier.monthly_gross_profit || 0) >= 0 ? '#531dab' : '#cf1322' }}>
                                {formatCurrency(supplier.monthly_gross_profit)}
                            </div>
                        </div>
                    </Col>
                    <Col span={6}>
                        <div style={{ background: '#f9f0ff', padding: 16, borderRadius: 8, border: '1px solid #d3adf7' }}>
                            <div style={{ color: '#722ed1', fontSize: 12, marginBottom: 4 }}>本年毛利</div>
                            <div style={{ fontSize: 20, fontWeight: 600, color: (supplier.yearly_gross_profit || 0) >= 0 ? '#531dab' : '#cf1322' }}>
                                {formatCurrency(supplier.yearly_gross_profit)}
                            </div>
                        </div>
                    </Col>
                    <Col span={6}>
                        <div style={{ background: '#e6f7ff', padding: 16, borderRadius: 8, border: '1px solid #91d5ff' }}>
                            <div style={{ color: '#1890ff', fontSize: 12, marginBottom: 4 }}>历史总毛利</div>
                            <div style={{ fontSize: 20, fontWeight: 600, color: (supplier.total_gross_profit || 0) >= 0 ? '#096dd9' : '#cf1322' }}>
                                {formatCurrency(supplier.total_gross_profit)}
                            </div>
                        </div>
                    </Col>
                    <Col span={6}>
                        <div style={{ background: '#fffbe6', padding: 16, borderRadius: 8, border: '1px solid #ffe58f' }}>
                            <div style={{ color: '#d48806', fontSize: 12, marginBottom: 4 }}>毛利率</div>
                            <div style={{ fontSize: 20, fontWeight: 600, color: '#ad6800' }}>
                                {(supplier.gross_profit_margin || 0).toFixed(2)}%
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* 财务情况 */}
            <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
                    <DollarOutlined style={{ fontSize: 18, color: '#fa8c16', marginRight: 8 }} />
                    <span style={{ fontSize: 16, fontWeight: 500 }}>财务情况</span>
                </div>
                <Row gutter={[16, 16]}>
                    <Col span={6}>
                        <div style={{ background: '#fff7e6', padding: 16, borderRadius: 8, border: '1px solid #ffd591' }}>
                            <div style={{ color: '#fa8c16', fontSize: 12, marginBottom: 4 }}>待付款金额</div>
                            <div style={{ fontSize: 20, fontWeight: 600, color: '#d46b08' }}>
                                {formatCurrency(supplier.outstanding_payment)}
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </Card>
    );
};

export default FinancialTab;
